import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex items-center gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-300">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[1px] w-0 bg-[var(--primary)] transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-6">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-6 w-32 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <nav className="flex flex-col p-6 gap-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium text-foreground/80 hover:text-[var(--primary)] transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 dark bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-[hsl(217,19%,20%)] py-3" : "bg-background py-5 border-b border-transparent")}>
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        {}
        <Link to="/" className="flex items-center gap-2 z-50">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555312/img/wpn_lightning_logo.png" alt="Web Performance News Logo" className="h-7 w-auto object-contain" />
        </Link>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="43">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 top-[60px] z-40 bg-background border-t border-[hsl(217,19%,20%)] md:hidden animate-in slide-in-from-top-5 fade-in duration-200">
          <MenuProvider menu_id="43">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}